import {
    ConnectorMetadataScope,
    IDynamicLinkedServiceTypeMetadata,
    ILinkedServiceTypeMetadata
} from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { Constants } from 'app/shared/shared-constants';
import { format } from 'app/shared/utils/string.util';
import {
    commonFileTypeStoreGetMetadataActivityReadSettings,
    commonFileTypeStoreWriteSettingControls,
    commonFileTypeStoreDeleteActivityReadSettings,
    commonBinaryStoreWriteSettingControls,
    getCommonBinaryStoreReadSettingControls,
    getCommonFileTypeStoreReadSettingControls,
    azureResourceSelectMethodOptions,
    cdcDestination
} from '../consts/metadata-consts';
import { binaryMetadata } from '../dataset/binary-metadata';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';

import {
    fileShareTextareaBase,
    parameterizableMetadataBase,
    sasUriSetting,
    storageEndpointSetting
} from './azure-storage-shared-fields';
import { AuthenticationType } from 'app/interfaces/IAuthenticationType';
import { ConnectionAuthKind, CredentialTypeOptions, azureStorageAuthTypes } from 'shell-models-lib';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { ISelectOption } from 'app/interfaces/ISelectOption';
import { DirectionalHint } from '@fluentui/react/lib/common/DirectionalHint';
import { storagePEWellknownErrorMessages } from 'app/shared/utils/connector-metadata-static-consts';
import { accountKeyConnectionStringSetting } from '../consts/metadata-consts-with-ec-deps';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { EnvironmentInjector } from '@angular/core';
import { combinedMIAuthenticationOption } from '../utls/authenticationUtils';
import { GlobalVariables } from 'app/services/globals';

const hostPlaceholder = 'e.g. \\\\ServerName\\SharedFolder\\[\\Folder], ' +
    '\\\\<storage name>.file.core.windows.net[\\file service name]';

const snapshotBase: ControlMetadata = {
    type: 'date-time-picker',
    bind: 'snapshot',
    label: stringResources.ModelName_Snapshot,
    drops: DirectionalHint.topLeftEdge,
    isLabelVertical: true,
    description: stringResources.Snapshot_Description
}

const dynamicControlsForBasicAuth: ControlMetadata = {
    type: 'radio-switch',
    nested: 1,
    label: stringResources.AccountSelectionMethodLabel,
    description: stringResources.AzureStorage_SelectMethodDescription,
    options: azureResourceSelectMethodOptions,
    isLabelVertical: true,
    defaultValue: '{host != null || parameterized ? "manual" : "azure"}',
    cases: [
        {
            caseValue: 'azure',
            children: [
                {
                    type: 'azure-storage-account-select',
                    bind: {
                        username: 'username',
                        accountKey: 'password',
                        host: 'host',
                        scope: '_accountResourceID',
                        primaryEndpoint: 'primaryEndpoint'
                    },
                    showPEStatus: true,
                    serviceType: 'file',
                    showFileShare: true,
                    validators: {
                        host: [
                            { type: 'required', errorMessage: '' }
                        ],
                        username: [
                            { type: 'required', errorMessage: '' }
                        ],
                        accountKey: [
                            { type: 'required', errorMessage: '' }
                        ]
                    }
                }
            ]
        },
        {
            caseValue: 'manual',
            children: [
                {
                    type: 'parameterizable',
                    child: {
                        type: 'textbox',
                        bind: 'host',
                        label: stringResources.ModelName_Host,
                        required: true,
                        validators: [
                            { type: 'required', errorMessage: '' },
                            {
                                type: 'pattern',
                                pattern: '^(?![hH][tT][tT][pP]:).*',
                                errorMessage: stringResources.FileSystem_HostStartWithHttp_ErrorMessage
                            },
                            {
                                type: 'pattern',
                                pattern: '^(?![fF][tT][pP]:).*',
                                errorMessage: stringResources.FileSystem_HostStartWithFtp_ErrorMessage
                            }
                        ],
                        isLabelVertical: true,
                        description: `${stringResources.FileSystem_Host_Description}\n${hostPlaceholder}`,
                        placeholder: hostPlaceholder
                    }
                },
                {
                    type: 'parameterizable',
                    child: {
                        type: 'textbox',
                        bind: 'username',
                        label: stringResources.ModelName_UserName,
                        required: true,
                        validators: [
                            { type: 'required', errorMessage: '' },
                            {
                                type: 'pattern',
                                pattern: '^((?!^[ ]+$).)*$',
                                errorMessage: stringResources.Empty_String_Error_Message
                            }
                        ],
                        isLabelVertical: true
                    }
                },
                {
                    type: 'dynamic-akv-switcher',
                    enableAKVParameterization: true,
                    bind: 'password',
                    label: stringResources.ModelName_Password,
                    child: {
                        type: 'parameterizable',
                        attribute: 'password',
                        disableAddParameter: true,
                        child: {
                            type: 'password',
                            label: stringResources.ModelName_Password,
                            required: true,
                            isLabelVertical: true
                        }
                    },
                    validators: [
                        { type: 'required', errorMessage: '' }
                    ],
                    description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template, stringResources.FileLinkedServiceLabel,
                        stringResources.ModelName_Password)
                }
            ]
        }
    ]
};

export const azuerFileBasicAuthOptions = [
    {
        label: stringResources.Basic_DisplayText,
        value: AuthenticationType.Basic
    }
];

export const azuerFileNewAuthOptions = [
    {
        label: stringResources.AuthenticationType_AccountKey,
        value: azureStorageAuthTypes.AccountKeyAuthentication
    },
    {
        label: stringResources.SasUri,
        value: azureStorageAuthTypes.SasUriAuthentication
    }
].concat(combinedMIAuthenticationOption);

export async function calAzureFileStorageMetadata(
    injector: EnvironmentInjector,
    authOptions: Array<ISelectOption<string>>,
    notShowInTypeSelection?: boolean,
    metadataScope?: ConnectorMetadataScope): Promise<ILinkedServiceTypeMetadata> {
    return {
        type: adfModelTypeMap.azureFileStorage.linkedServiceType,
        displayName: stringResources.AzureFileStorage_DisplayText,
        supportOPDG: GlobalVariables.enableOPDG,
        supportedInTrident: true,
        supportCopyJobSource: true,
        supportCopyJobSink: true,
        ...(GlobalVariables.copyJobGenericCdcPhase2 ? {
            cdc: {
                supportCDC: true,
                supportedCDCDestinations: cdcDestination,
                supportGenericCdc: true
            }
        } : {}),
        supportAutoFillCopilotInput: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.Extension,
            resourceKind: 'AzureFiles' as ResourceKind
        },
        supportedAuthKindsInTrident: [ConnectionAuthKind.Key],
        linkedServiceKind: 'file',
        icon: {
            className: 'svg-i-100x100-AzureFileStorage'
        },
        isFirstClass: true,
        supportPE: true,
        peGroupIDs: ['file'],
        peWellknowErrorMessages: [
            ...storagePEWellknownErrorMessages,
            /Access is denied/i
        ],
        resourceTypes: ['Microsoft.ClassicStorage/storageAccounts', 'Microsoft.Storage/storageAccounts'],
        supportIncrementalBinaryCopy: true,
        supportDatasetV2: true,
        copySinkTypes: [],
        copySourceTypes: [],
        supportRootFolder: true,
        supportGetMetadataActivity: true,
        locationPattern: {
            folderPath: '([^]*)/[^/]*',
            fileName: '(?:[^]*/)?([^/]*)'
        },
        mergeReadSettingsInTrident: true,
        locationPropertiesRequireParameters: ['folderPath', 'fileName'],
        pathRegex: {
            pattern: Constants.fileSharePathValidation,
            message: stringResources.CopyWizard_Dataset_File_Sink_FileSharePathValidationError
        },
        getMetadataActivityReadProperties: commonFileTypeStoreGetMetadataActivityReadSettings,
        deleteActivityReadProperties: commonFileTypeStoreDeleteActivityReadSettings,
        storeReadSettingControls: getCommonFileTypeStoreReadSettingControls(true),
        storeWriteSettingControls: commonFileTypeStoreWriteSettingControls,
        datasetTypeToStoreReadSettingControls: {
            [binaryMetadata.type]: getCommonBinaryStoreReadSettingControls(true)
        },
        datasetTypeToStoreWriteSettingControls: {
            [binaryMetadata.type]: commonBinaryStoreWriteSettingControls
        },
        category: ['Azure'],
        notShowInTypeSelection,
        metadataScope,
        supportFirstClassParameterExperience: true,
        controls: [
            {
                type: 'select-switch',
                bind: 'authenticationMethod',
                label: stringResources.Authentication_Method_Label,
                options: authOptions,
                isLabelVertical: true,
                defaultValue: 'AccountKeyAuthentication',
                cases: [
                    {
                        caseValue: azureStorageAuthTypes.AccountKeyAuthentication,
                        children: [
                            accountKeyConnectionStringSetting('file', true, true),
                            snapshotBase
                        ]
                    },
                    {
                        caseValue: azureStorageAuthTypes.SasUriAuthentication,
                        children: [
                            sasUriSetting(false, true, true),
                            snapshotBase
                        ]
                    },
                    {
                        caseValue: AuthenticationType.Basic,
                        children: [
                            {
                                type: 'hint',
                                description: stringResources.AzureFileStorageBasicAuthType_Warning,
                                link: 'https://aka.ms/connector-azure-file-storage',
                                dynamicContentTextClass: 'remove-top-margin'
                            },
                            dynamicControlsForBasicAuth
                        ]
                    },
                    {
                        caseValue: azureStorageAuthTypes.MSIAuthentication,
                        children: [
                            storageEndpointSetting('file'),
                            {
                                type: 'management-identity-info',
                                resourceType: stringResources.AzureFileStorage_DisplayText,
                                link: 'https://learn.microsoft.com/azure/data-factory/connector-azure-file-storage?tabs=data-factory#system-assigned-managed-identity-authentication'
                            },
                            parameterizableMetadataBase(fileShareTextareaBase, true),
                            snapshotBase
                        ]
                    },
                    {
                        // for UAMI there won't be any radio buttons, since inline mode is not supported.
                        caseValue: azureStorageAuthTypes.UserAssignedManagedIdentity,
                        children: [
                            storageEndpointSetting('file'),
                            {
                                type: 'credential-select',
                                bind: 'credential',
                                filterCredentialType: CredentialTypeOptions.UserAssignedManagedIdentity,
                                required: true,
                                validators: [{ type: 'required', errorMessage: '' }]
                            },
                            parameterizableMetadataBase(fileShareTextareaBase, true),
                            snapshotBase
                        ]
                    }
                ]
            }

        ]
    };
}

export const azureFileStorageMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    return await calAzureFileStorageMetadata(injector, azuerFileNewAuthOptions.concat(azuerFileBasicAuthOptions));
};
